<template>
  <div>
    <h1 class="page-title">Dashboard</h1>

    <SkeletonLoader v-if="store.loading && store.acampantes.length === 0" variant="stat-card" :count="4" />

    <template v-else>
      <!-- Stat cards -->
      <div class="stats-grid">
        <div class="stat-card card">
          <div class="stat-icon stat-total">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M16 17v2H2v-2s0-4 7-4s7 4 7 4m-3.5-9.5A3.5 3.5 0 1 0 9 11a3.5 3.5 0 0 0 3.5-3.5m3.44 5.5A5.32 5.32 0 0 1 18 17v2h4v-2s0-3.63-6.06-4M15 4a3.39 3.39 0 0 0-1.93.59a5 5 0 0 1 0 5.82A3.39 3.39 0 0 0 15 11a3.5 3.5 0 0 0 0-7Z"/></svg>
          </div>
          <div class="stat-info">
            <span class="stat-number">{{ totalCount }}</span>
            <span class="stat-label">Total Inscritos</span>
          </div>
        </div>

        <div class="stat-card card">
          <div class="stat-icon stat-confirmed">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="m10.6 16.6l7.05-7.05l-1.4-1.4l-5.65 5.65l-2.85-2.85l-1.4 1.4l4.25 4.25ZM12 22q-2.075 0-3.9-.788t-3.175-2.137q-1.35-1.35-2.137-3.175T2 12q0-2.075.788-3.9t2.137-3.175q1.35-1.35 3.175-2.137T12 2q2.075 0 3.9.788t3.175 2.137q1.35 1.35 2.138 3.175T22 12q0 2.075-.788 3.9t-2.137 3.175q-1.35 1.35-3.175 2.138T12 22Z"/></svg>
          </div>
          <div class="stat-info">
            <span class="stat-number">{{ confirmedCount }}</span>
            <span class="stat-label">Confirmados</span>
          </div>
        </div>

        <div class="stat-card card">
          <div class="stat-icon stat-pending">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10s10-4.5 10-10S17.5 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8s8 3.59 8 8s-3.59 8-8 8m.5-13H11v6l5.2 3.2l.8-1.3l-4.5-2.7V7Z"/></svg>
          </div>
          <div class="stat-info">
            <span class="stat-number">{{ pendingCount }}</span>
            <span class="stat-label">Pendentes</span>
          </div>
        </div>

        <div class="stat-card card">
          <div class="stat-icon stat-percent">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2m-7 2h1.5v3l2-3h1.7l-2 3l2 3h-1.7l-2-3v3H12V5zM7 7.25h2.5V6.5H7V5h4v3.75H8.5v.75H11V11H7V7.25zM19 19H5L19 5v14z"/></svg>
          </div>
          <div class="stat-info">
            <span class="stat-number">{{ checkInPercent }}%</span>
            <span class="stat-label">Check-in</span>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="charts-grid">
        <section class="chart-card card">
          <h2 class="section-title">Confirmados por Equipe</h2>
          <EquipeBars v-if="totalCount > 0" :acampantes="store.acampantes" />
          <p v-else class="chart-empty">Cadastre acampantes para visualizar.</p>
        </section>

        <section class="chart-card card">
          <h2 class="section-title">Transporte</h2>
          <TransporteDonut :acampantes="store.acampantes" />
        </section>
      </div>

      <!-- Ranking + recentes -->
      <div class="bottom-grid">
        <section class="chart-card card">
          <h2 class="section-title">Ranking de Check-ins</h2>
          <div class="ranking" v-if="ranking.length > 0">
            <div class="ranking-item" v-for="(r, i) in ranking" :key="r.name">
              <span class="rank-pos" :class="`rank-${i + 1}`">{{ i + 1 }}</span>
              <span class="rank-name">{{ r.name }}</span>
              <div class="rank-bar"><div class="rank-fill" :style="{ width: rankPct(r.count) }"></div></div>
              <span class="rank-count">{{ r.count }}</span>
            </div>
          </div>
          <p v-else class="chart-empty">Nenhum check-in registrado ainda.</p>
        </section>

        <section class="chart-card card">
          <h2 class="section-title">Últimos Check-ins</h2>
          <div class="recent-list" v-if="recent.length > 0">
            <div class="recent-item" v-for="item in recent" :key="item.id">
              <div class="recent-main">
                <span class="recent-name">{{ item.name }}</span>
                <div class="recent-tags">
                  <EquipeChip :equipe="item.equipe" />
                  <TransporteBadge :value="item.transporte" size="small" />
                </div>
              </div>
              <div class="recent-meta">
                <span v-if="item.checkedBy" class="recent-by">{{ item.checkedBy.name }}</span>
                <span v-if="item.checkedAt" class="recent-time">{{ formatTime(item.checkedAt) }}</span>
              </div>
            </div>
          </div>
          <p v-else class="chart-empty">Confirme o primeiro check-in para aparecer aqui.</p>
        </section>
      </div>
    </template>
  </div>
</template>

<script>
import { useAcampantesStore } from '../stores/acampantes'
import EquipeChip from '../components/EquipeChip.vue'
import TransporteBadge from '../components/TransporteBadge.vue'
import EquipeBars from '../components/charts/EquipeBars.vue'
import TransporteDonut from '../components/charts/TransporteDonut.vue'
import SkeletonLoader from '../components/ui/SkeletonLoader.vue'

export default {
  name: 'DashboardPage',
  components: { EquipeChip, TransporteBadge, EquipeBars, TransporteDonut, SkeletonLoader },
  computed: {
    store() { return useAcampantesStore() },
    totalCount() { return this.store.totalCount },
    confirmedCount() { return this.store.confirmedCount },
    pendingCount() { return this.store.pendingCount },
    checkInPercent() {
      return this.totalCount > 0 ? Math.round((this.confirmedCount / this.totalCount) * 100) : 0
    },
    recent() {
      return this.store.confirmed
        .slice()
        .sort((a, b) => {
          const da = a.checkedAt ? new Date(a.checkedAt).getTime() : 0
          const db = b.checkedAt ? new Date(b.checkedAt).getTime() : 0
          return db - da
        })
        .slice(0, 8)
    },
    ranking() {
      const map = new Map()
      for (const a of this.store.confirmed) {
        const name = a.checkedBy?.name || 'Sem login'
        map.set(name, (map.get(name) || 0) + 1)
      }
      return Array.from(map.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
    },
    maxRankCount() {
      return Math.max(1, ...this.ranking.map(r => r.count))
    }
  },
  methods: {
    formatTime(iso) {
      try {
        return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      } catch { return '' }
    },
    rankPct(count) {
      return `${Math.round((count / this.maxRankCount) * 100)}%`
    }
  }
}
</script>

<style scoped>
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-base);
  margin-bottom: var(--spacing-xl);
}
.stat-card {
  display: flex;
  align-items: center;
  gap: var(--spacing-base);
  padding: var(--spacing-base) var(--spacing-lg);
}
.stat-icon {
  width: 48px; height: 48px;
  border-radius: var(--radius-lg);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.stat-total { background: rgba(51, 102, 204, 0.1); color: var(--color-accent); }
.stat-confirmed { background: rgba(46, 204, 113, 0.1); color: var(--color-success); }
.stat-pending { background: rgba(243, 156, 18, 0.15); color: var(--color-warning); }
.stat-percent { background: rgba(124, 58, 237, 0.12); color: #7c3aed; }
.stat-info { display: flex; flex-direction: column; }
.stat-number {
  font-size: var(--text-3xl);
  font-weight: 800;
  color: var(--color-text-primary);
  line-height: 1;
}
.stat-label {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
  margin-top: var(--spacing-xs);
}

.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: var(--spacing-base);
  margin-bottom: var(--spacing-base);
}
.chart-card { padding: var(--spacing-lg); }
.section-title {
  font-size: var(--text-md);
  font-weight: 700;
  color: var(--color-text-heading);
  margin: 0 0 var(--spacing-base);
}
.chart-empty {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
  text-align: center;
  padding: var(--spacing-lg) 0;
}

.bottom-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: var(--spacing-base);
}

.ranking { display: flex; flex-direction: column; gap: var(--spacing-md); }
.ranking-item {
  display: grid;
  grid-template-columns: 32px 100px 1fr 32px;
  align-items: center;
  gap: var(--spacing-md);
}
.rank-pos {
  width: 28px; height: 28px;
  border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: var(--text-sm);
  font-weight: 800;
  background: var(--color-bg-hover);
  color: var(--color-text-muted);
}
.rank-1 { background: #f1c40f; color: #fff; }
.rank-2 { background: #95a5a6; color: #fff; }
.rank-3 { background: #cd7f32; color: #fff; }
.rank-name {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rank-bar {
  height: 22px;
  background: var(--color-bg-hover);
  border-radius: var(--radius-md);
  overflow: hidden;
}
.rank-fill {
  height: 100%;
  background: var(--color-accent);
  border-radius: var(--radius-md);
  transition: width 0.5s ease;
}
.rank-count {
  font-size: var(--text-md);
  font-weight: 800;
  color: var(--color-accent);
  text-align: right;
}

.recent-list { display: flex; flex-direction: column; gap: var(--spacing-sm); }
.recent-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) 0;
  border-bottom: 1px solid var(--color-bg-separator);
}
.recent-item:last-child { border-bottom: none; }
.recent-main { display: flex; flex-direction: column; gap: 4px; min-width: 0; flex: 1; }
.recent-name {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.recent-tags { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.recent-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}
.recent-by {
  color: var(--color-accent);
  font-weight: 700;
}

@media (max-width: 600px) {
  .ranking-item { grid-template-columns: 28px 80px 1fr 28px; gap: var(--spacing-sm); }
}
</style>
